/**
 * useCategoryName - Hook for language-aware category name display
 * Returns category names based on user's selected language in react-i18next
 */
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

// Interface for categories with localized names
export interface LocalizedCategory {
  name: string
  nameLocalized?: {
    en?: string
    id?: string
  }
}

/**
 * Hook that provides language-aware category name retrieval
 * Uses react-i18next to determine the current language
 *
 * @example
 * const { getCategoryName } = useCategoryName()
 * return <span>{getCategoryName(category)}</span>
 */
export function useCategoryName() {
  const { i18n } = useTranslation()

  /**
   * Get the category name in the current language
   * Falls back to: current language → English → Indonesian → default name
   */
  const getCategoryName = useCallback((category: LocalizedCategory): string => {
    if (category.nameLocalized) {
      const lang = i18n.language as 'en' | 'id'
      // Try current language first, then fallback chain
      return category.nameLocalized[lang] ||
             category.nameLocalized.en ||
             category.nameLocalized.id ||
             category.name
    }
    return category.name
  }, [i18n.language])

  /**
   * Get the category path as a localized string
   * Joins category names with " > " separator
   */
  const getCategoryPathName = useCallback((categories: LocalizedCategory[]): string => {
    return categories.map(cat => getCategoryName(cat)).join(' > ')
  }, [getCategoryName])

  return {
    getCategoryName,
    getCategoryPathName,
    currentLanguage: i18n.language
  }
}

export default useCategoryName
